import { useCallback } from 'react'
import { type Icon } from '@tabler/icons-react'
import { useTutorial } from '@/contexts/tutorialContext.tsx'

export interface PortalTab {
  id: string
  label: string
  icon?: Icon
  spotId?: string
}

interface PortalTabsProps {
  tabs: PortalTab[]
  activeTab: string
  onTabChange: (tabId: string) => void
}

export default function PortalTabs({ tabs, activeTab, onTabChange }: PortalTabsProps) {
  return (
    <div className="overflow-x-auto mb-6">
      <div role="tablist" className="tabs tabs-bordered w-fit">
        {tabs.map(tab => (
          <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id} onClick={() => onTabChange(tab.id)} />
        ))}
      </div>
    </div>
  )
}

function TabButton({ tab, isActive, onClick }: { tab: PortalTab; isActive: boolean; onClick: () => void }) {
  const registerSpot = useTutorial(s => s.registerSpot)
  const unregisterSpot = useTutorial(s => s.unregisterSpot)

  const callbackRef = useCallback((node: HTMLButtonElement | null) => {
    if (!tab.spotId) return
    if (node) registerSpot(tab.spotId, node)
    else unregisterSpot(tab.spotId)
  }, [tab.spotId, registerSpot, unregisterSpot])

  return (
    <button
      ref={tab.spotId ? callbackRef : undefined}
      role="tab"
      className={`tab ${isActive ? 'tab-active !border-primary font-medium' : 'opacity-60 hover:opacity-100'}`}
      onClick={onClick}
    >
      <span className="flex items-center gap-2 whitespace-nowrap">
        {tab.icon && <tab.icon size={16} className="flex-shrink-0" />}
        {tab.label}
      </span>
    </button>
  )
}
